/// <amd-module name="VSSUI/Components/DatePicker/DatePicker" />

import * as React from "react";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { localeDateFormat } from "VSS/Platform/Date";
import { WrappedComponent } from "VSS/Platform/Layout";
import { Callout, ContentLocation } from "VSSUI/Callout";
import { IIconProps } from "VSSUI/Icon";
import { FocusZoneKeyStroke, IFocusZoneProps } from "VSSUI/FocusZone";
import { FormItem } from "VSSUI/FormItem";
import { Observer } from "VSSUI/Observer";
import * as Resources from "VSSUI/Resources.DatePicker";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { css, KeyCode } from "VSSUI/Util";
import { Location } from "VSSUI/Utilities/Position";
import { CustomizedScreenSizeObserver } from "VSSUI/Utils/CustomizedScreenSize";
import { IDatePickerProps, IDatePickerManualEditingOptions, DatePickerType } from "./DatePicker.Props";
import { datePickerStrings } from "./DateStringHelper";

export interface DatePickerState {
    pickerShown: boolean;
    dateStr?: string;
}

export class DatePicker extends React.Component<IDatePickerProps, DatePickerState> {
    private containerDiv = React.createRef<HTMLDivElement>();
    constructor(props: IDatePickerProps) {
        super(props);

        this.state = {
            pickerShown: false,
            dateStr: undefined
        };
    }

    public componentDidMount(): void {
        if (this.props.expandOnMount) {
            this.setState({ pickerShown: true });
        }
    }

    public render(): JSX.Element {
        const { containerClassName } = this.props;
        const focusZoneProps: IFocusZoneProps = { preprocessKeyStroke: e => FocusZoneKeyStroke.IgnoreAll, ...this.props.focusZoneProps };
        return (
            <Observer date={this.props.value} manualEditingOptions={this.props.manualEditingOptions}>
                {(props: { date?: Date | string, manualEditingOptions?: IDatePickerManualEditingOptions }) => {
                    return (
                        <div className={containerClassName} ref={this.containerDiv}>
                            <FormItem error={this.props.hasError} message={this.props.errorMessage}>
                                <TextField
                                    ariaControls={this.state.pickerShown ? this.props.calendarId : undefined}
                                    ariaExpanded={this.state.pickerShown}
                                    ariaHasPopup={true}
                                    ariaLabel={this.props.ariaLabel}
                                    ariaLabelledBy={this.props.ariaLabelledBy}
                                    className={css("flex-row flex-grow", this.props.className)}
                                    disabled={this.props.disabled}
                                    inputClassName="bolt-date-picker-input"
                                    inputId={this.props.inputId}
                                    onClick={props.manualEditingOptions ? undefined : this.onClick}
                                    onKeyDown={this.onTextFieldKeyDown}
                                    onChange={props.manualEditingOptions ? this.onChange : undefined}
                                    readOnly={props.manualEditingOptions ? false : true}
                                    role="combobox"
                                    suffixIconProps={this.getSuffixIconProps(props.manualEditingOptions)}
                                    placeholder={this.props.placeholder}
                                    value={this.getValue(props)}
                                />
                            </FormItem>
                            {this.state.pickerShown && (
                                <CustomizedScreenSizeObserver>
                                    {(screenSizeProps: { screenSize: ScreenSize }) => {
                                        const smallScreen = screenSizeProps.screenSize === ScreenSize.xsmall;
                                        return (
                                            <Callout
                                                anchorElement={!smallScreen ? this.containerDiv.current ?? undefined : undefined}
                                                anchorOrigin={!smallScreen ? { horizontal: Location.start, vertical: Location.end } : undefined}
                                                ariaLabel={this.props.calloutAriaLabel || Resources.DatePickerCalloutLabel}
                                                contentClassName="depth-8 bolt-date-picker"
                                                contentLocation={smallScreen ? ContentLocation.Start : undefined}
                                                contentShadow={true}
                                                id={this.props.calendarId}
                                                onDismiss={this.calendarDismissed}
                                                escDismiss={true}
                                                lightDismiss={true}
                                                focuszoneProps={focusZoneProps}
                                            >
                                                <WrappedComponent
                                                    className={"bolt-date-picker"}
                                                    dependencies={["ms.vss-features.ui-calendar-content"]}
                                                    loadingComponent={this.renderSpinner}
                                                    wrappedProps={{
                                                        onSelectDate: this.onSelectDate,
                                                        onDismiss: this.calendarDismissed,
                                                        shouldFocusOnMount: true,
                                                        showClear: this.props.showClear,
                                                        minDate: this.props.minDate,
                                                        maxDate: this.props.maxDate,
                                                        value: this.getDateOrUndefined(props.date),
                                                        strings: datePickerStrings()
                                                    }}
                                                    wrappedType="fabric.pickers.calendar"
                                                />
                                            </Callout>
                                        );
                                    }}
                                </CustomizedScreenSizeObserver>
                            )}
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private calendarDismissed = (): void => {
        this.hidePicker();
    };

    private onClick = (event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!event.defaultPrevented && !this.props.disabled) {
            this.setState({ pickerShown: true });
            event.preventDefault();
        }
    };

    private onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, dateStr: string) => {
        this.setState({ dateStr });
        this.props.debounceOnChange?.(dateStr);
    }

    private onTextFieldKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented && !this.props.disabled) {
            switch (event.which) {
                case KeyCode.downArrow:
                case KeyCode.enter:
                    this.setState({ pickerShown: true });
                    event.preventDefault();
                    break;
                case KeyCode.escape:
                    this.hidePicker();
                    event.preventDefault();
                    break;
                case KeyCode.backspace:
                case KeyCode.delete:
                    if (this.props.showClear && !this.props.manualEditingOptions?.value) {
                        this.props.onChange?.(undefined);
                    }
            }
        }
    };

    private onSelectDate = (date: Date): void => {
        this.hidePicker();
        this.props.onChange?.(date);
    };

    private getDateOrUndefined(date?: string | Date): Date | undefined {
        return date instanceof Date ? date : undefined;
    }

    private hidePicker() {
        this.setState({ pickerShown: false });
        this.props.onCollapse?.();
    }

    private renderSpinner = () => {
        return (
            <div className="bolt-date-picker-spinner flex-row flex-center justify-center">
                <Spinner label={Resources.Loading} size={SpinnerSize.large} />
            </div>
        );
    };

    private toLocaleTimeString(date?: Date): string | undefined {
        return date instanceof Date ? localeDateFormat(date, "t", new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "numeric" })) : undefined;
    }

    private getValue(props: { date?: Date | string, manualEditingOptions?: IDatePickerManualEditingOptions }): string | undefined {
        if (props.manualEditingOptions) {
            if (this.state.dateStr) {
                return this.state.dateStr
            }
            if (props.manualEditingOptions.datePickerType === DatePickerType.Time) {
                if (props.date instanceof Date) {
                    return this.toLocaleTimeString(this.getDateOrUndefined(props.date));
                } else if (this.props.hasError) {   
                    return props.date;
                }
            }
        }

        if (props.date) {
            if (props.date instanceof Date) {
                return localeDateFormat(props.date, "d", new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric" }));
            } else if (this.props.hasError) {
                return props.date;
            }
        } else {
            return undefined;
        }
    }

    private getSuffixIconProps(manualEditingOptions?: IDatePickerManualEditingOptions): IIconProps {
        return {
            iconName: manualEditingOptions?.datePickerType === DatePickerType.Time ? "Clock" : "Calendar",
            onClick: manualEditingOptions?.datePickerType === DatePickerType.Time ? undefined : this.onClick
        }
    }
}
